import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { fileUrl } from "../site/HeroSlides";

const tf = (f) => `'${f}', serif`;

function Particles({ color }) {
  const bits = useMemo(() => Array.from({ length: 40 }, (_, i) => ({ x: ((i * 53) % 200) - 100, y: ((i * 31) % 120) - 60, s: 2 + (i % 4), dl: (i % 10) * 0.08 })), []);
  return bits.map((b, i) => (
    <motion.span key={i} className="absolute left-1/2 top-1/2 rounded-full pointer-events-none" style={{ width: b.s, height: b.s, background: color, boxShadow: `0 0 8px ${color}` }}
      initial={{ x: b.x * 2.2, y: b.y * 3, opacity: 0 }} animate={{ x: b.x * 0.6, y: b.y * 0.3, opacity: [0, 1, 0.8, 0] }} transition={{ duration: 2.4, delay: b.dl, repeat: Infinity, repeatDelay: 1.5 }} />
  ));
}

function Fireworks({ color }) {
  const bursts = [[-70, -40, 0], [80, -60, 0.6], [0, -90, 1.2], [-40, 40, 1.8], [70, 30, 2.3]];
  return bursts.map(([x, y, dl], i) => (
    <span key={i} className="absolute left-1/2 top-1/2 pointer-events-none" style={{ transform: `translate(${x}px, ${y}px)` }}>
      {Array.from({ length: 12 }, (_, k) => (
        <motion.span key={k} className="absolute w-1 h-1 rounded-full" style={{ background: k % 2 ? color : "#fff", boxShadow: `0 0 6px ${color}` }}
          initial={{ x: 0, y: 0, opacity: 0 }} animate={{ x: Math.cos((k / 12) * 6.283) * 46, y: Math.sin((k / 12) * 6.283) * 46, opacity: [0, 1, 0] }} transition={{ duration: 1.3, delay: dl, repeat: Infinity, repeatDelay: 1.6 }} />
      ))}
    </span>
  ));
}

export function NamesFx({ fx, names, d, className = "text-5xl" }) {
  if (fx === "handwriting") return (
    <svg viewBox="0 0 600 120" className="w-full max-w-sm h-24" data-testid="names-fx-handwriting">
      <text x="50%" y="70%" textAnchor="middle" className="inv-handwrite" style={{ fontFamily: tf("Great Vibes"), fontSize: names.length > 18 ? 56 : 72, stroke: d.pal.accent, fill: d.pal.text, "--c": d.pal.text }}>{names}</text>
    </svg>
  );
  const letters = fx === "gold-particles" ? names.split("") : null;
  return (
    <div className="relative" data-testid={`names-fx-${fx || "none"}`}>
      {fx === "gold-particles" && <Particles color={d.pal.accent} />}
      {fx === "fireworks" && <Fireworks color={d.pal.accent} />}
      <h1 className={`${className} leading-tight relative`} style={{ fontFamily: tf(d.font), textShadow: fx === "gold-particles" ? `0 0 18px ${d.pal.accent}88` : "none" }} data-testid="invite-names">
        {letters ? letters.map((c, i) => <motion.span key={i} className="inline-block" initial={{ opacity: 0, y: -20, filter: "blur(6px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }} transition={{ delay: 1.2 + i * 0.05, duration: 0.6 }}>{c === " " ? "\u00a0" : c}</motion.span>) : names}
      </h1>
    </div>
  );
}

function SceneBody({ s, d, inv, wedding, i }) {
  const media = inv.media || [];
  const m = media.length ? media[i % media.length] : null;
  if (s.type === "photos") return (
    <div className="grid grid-cols-2 gap-2 w-full max-w-xs" data-testid="scene-photos">
      {(media.length ? media.slice(0, 4) : []).map((x, k) => (
        <motion.div key={x.media_id} initial={{ opacity: 0, scale: 0.8, rotate: k % 2 ? 4 : -4 }} animate={{ opacity: 1, scale: 1, rotate: k % 2 ? 2 : -2 }} transition={{ delay: 0.2 + k * 0.2 }} className="aspect-square rounded-xl overflow-hidden border-2" style={{ borderColor: d.pal.accent }}>
          {x.type === "video" ? <video src={fileUrl(x.path)} autoPlay muted loop playsInline className="w-full h-full object-cover" /> : <img src={fileUrl(x.path)} alt="" className="w-full h-full object-cover" />}
        </motion.div>
      ))}
      {!media.length && <p className="col-span-2 text-xs opacity-70">Add photos of you two to fill this scene.</p>}
    </div>
  );
  if (s.type === "quote") return <p className="text-2xl leading-snug italic max-w-xs" style={{ fontFamily: tf("Cormorant Garamond") }}>“{s.text}”</p>;
  if (s.type === "details") {
    const date = wedding.wedding_date ? new Date(`${wedding.wedding_date}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" }) : "";
    return <div className="space-y-2"><p className="text-sm uppercase tracking-[0.25em]">{date}</p>{wedding.place && <p className="text-sm opacity-85">{wedding.place}</p>}{s.text && <p className="text-base opacity-90 max-w-xs mt-3" style={{ fontFamily: tf("Cormorant Garamond") }}>{s.text}</p>}</div>;
  }
  return (
    <div className="space-y-4 flex flex-col items-center">
      {m && <motion.div initial={{ scale: 1.15, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1.4 }} className="w-56 aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">{m.type === "video" ? <video src={fileUrl(m.path)} autoPlay muted loop playsInline className="w-full h-full object-cover" /> : <img src={fileUrl(m.path)} alt="" className="w-full h-full object-cover" />}</motion.div>}
      <p className="text-base leading-relaxed max-w-xs opacity-90" style={{ fontFamily: tf("Cormorant Garamond") }}>{s.text}</p>
    </div>
  );
}

// Short-film mode: each scene plays for a few seconds, guests tap to skip; the last step is the full invite with RSVP.
export function SceneStory({ d, inv, wedding, bg, children, onProgress = () => {} }) {
  const scenes = (inv.scenes || []).filter((s) => s.type !== "rsvp");
  const [i, setI] = useState(0);
  useEffect(() => { setI(0); }, [scenes.length]);
  useEffect(() => {
    onProgress(i >= scenes.length ? "finish" : "scene", Math.min(i + 1, scenes.length), scenes.length);
    if (i >= scenes.length) return undefined;
    const next = () => setI((x) => (x === i ? x + 1 : x));
    const v = scenes[i].voice_path;
    if (!v) { const t = setTimeout(next, 6500); return () => clearTimeout(t); }
    const a = new Audio(fileUrl(v));
    let t = setTimeout(next, 60000);
    a.onended = () => { clearTimeout(t); t = setTimeout(next, 900); };
    const s = setTimeout(() => a.play().catch(() => { clearTimeout(t); t = setTimeout(next, 6500); }), i === 0 ? 1500 : 400);
    return () => { a.pause(); clearTimeout(t); clearTimeout(s); };
  }, [i, scenes.length]); // eslint-disable-line react-hooks/exhaustive-deps
  if (i >= scenes.length) return children;
  const s = scenes[i];
  return (
    <div className="absolute inset-0 z-[5]" style={{ ...bg, color: d.pal.text }} data-testid="scene-story">
      <div className="absolute top-3 inset-x-3 flex gap-1 z-10">{[...scenes, 0].map((_, k) => <span key={k} className="h-0.5 flex-1 rounded" style={{ background: k <= i ? d.pal.accent : `${d.pal.text}33` }} />)}</div>
      <AnimatePresence mode="wait">
        <motion.div key={i} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0, transition: { duration: 0.7, delay: i === 0 ? 1.3 : 0 } }} exit={{ opacity: 0, y: -24, transition: { duration: 0.35 } }} className="absolute inset-0 flex flex-col items-center justify-center text-center px-7 gap-4" data-testid={`scene-${i}`}>
          <p className="text-[10px] uppercase tracking-[0.35em]" style={{ color: d.pal.accent }}>Chapter {i + 1}</p>
          {s.title && <h2 className="text-3xl leading-tight" style={{ fontFamily: tf(d.font) }}>{s.title}</h2>}
          <SceneBody s={s} d={d} inv={inv} wedding={wedding} i={i} />
          {s.voice_path && <p className="text-[10px] uppercase tracking-[0.3em] opacity-70 flex items-center gap-1" data-testid="scene-voice-playing"><span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: d.pal.accent }} /> In our own words</p>}
        </motion.div>
      </AnimatePresence>
      <button className="absolute inset-y-0 left-0 w-1/3 z-[6]" onClick={() => setI(Math.max(0, i - 1))} aria-label="Previous scene" data-testid="scene-prev" />
      <button className="absolute inset-y-0 right-0 w-2/3 z-[6]" onClick={() => setI(i + 1)} aria-label="Next scene" data-testid="scene-next" />
    </div>
  );
}
